import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { FaUsers, FaGlassCheers, FaArrowLeft, FaCalendarAlt, FaClock, FaUser } from "react-icons/fa";

import {
  listEvents,
  getEventDetails,
  reserveEvent,
  getAvailability,
  createReservation,
} from "../actions/reservationActions";
import { t } from "../i18n/translations";
import Loader from "../components/Loader";
import Message from "../components/Message";
import EmptyState from "../components/EmptyState";

import "./ReservationForm.css";
import "./ReservationScreen.css";
import "./EventsScreen.css";
import "./EventDetailScreen.css";
import "./ReservationHubScreen.css";

const emptyGuestForm = { guest_name: "", guest_email: "", guest_phone: "", guest_note: "" };

// Small numbered step header — icon + label — shared by the four steps of
// the table-reservation flow, so the guest always sees where they are and
// what's left, instead of a flat stack of same-weight fields.
function StepHeader({ number, icon, children }) {
  return (
    <div className="step-header">
      <span className="step-badge">{number}</span>
      {icon}
      <span className="step-label-text">{children}</span>
    </div>
  );
}

// Local calendar date, not toISOString()'s UTC date — reservation dates
// are naive local wall-clock values, so this must match the browser's
// local timezone or it drifts a day off near midnight.
const todayStr = () => {
  const d = new Date();
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 10);
};

function ReservationHubScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { language } = useSelector((state) => state.language);
  const localeTag = language === "hu" ? "hu-HU" : language;

  const query = new URLSearchParams(location.search);
  const eventSlugFromUrl = query.get("event");

  const [selectedSlug, setSelectedSlug] = useState(eventSlugFromUrl);
  const eventsSectionRef = useRef(null);

  // A direct link to a specific event (?event=slug) should land the
  // guest on that event, not the top of the page — scrolls the events
  // section into view once after mount.
  useEffect(() => {
    if (eventSlugFromUrl) {
      eventsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===== EVENTS LIST ===== */
  const { loading: loadingEvents, error: eventsError, events = [] } = useSelector(
    (state) => state.eventsList || {},
  );

  useEffect(() => {
    dispatch(listEvents());
  }, [dispatch]);

  /* ===== EVENT DETAIL ===== */
  const { loading: loadingEventDetail, event } = useSelector((state) => state.eventDetails || {});

  useEffect(() => {
    if (selectedSlug) dispatch(getEventDetails(selectedSlug));
  }, [dispatch, selectedSlug]);

  const openEvent = (slug) => {
    setSelectedSlug(slug);
    navigate(`/reservation?event=${slug}`, { replace: true });
  };

  const closeEvent = () => {
    setSelectedSlug(null);
    navigate("/reservation", { replace: true });
  };

  /* ===== EVENT BOOKING FORM ===== */
  const [eventForm, setEventForm] = useState(emptyGuestForm);
  const [eventPartySize, setEventPartySize] = useState(1);
  const { loading: reservingEvent, error: eventReserveError } = useSelector(
    (state) => state.eventReserve || {},
  );

  useEffect(() => {
    if (event) setEventPartySize(event.min_party_size || 1);
  }, [event]);

  const handleEventFormChange = (e) => {
    const { name, value } = e.target;
    setEventForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitEventBooking = async (e) => {
    e.preventDefault();
    try {
      const reservation = await dispatch(
        reserveEvent(selectedSlug, { ...eventForm, party_size: eventPartySize }),
      );
      navigate("/reservations/success", { state: { reservation } });
    } catch {
      // error already surfaced via eventReserveError
    }
  };

  /* ===== NORMAL RESERVATION FORM ===== */
  const [date, setDate] = useState(todayStr());
  const [partySize, setPartySize] = useState(2);
  const [selectedTime, setSelectedTime] = useState(null);
  const [reservationForm, setReservationForm] = useState(emptyGuestForm);

  const { loading: loadingSlots, error: slotsError, slots = [] } = useSelector(
    (state) => state.availability || {},
  );
  const { loading: creatingReservation, error: createError } = useSelector(
    (state) => state.reservationCreate || {},
  );

  useEffect(() => {
    setSelectedTime(null);
    dispatch(getAvailability(date, partySize));
  }, [dispatch, date, partySize]);

  const handleReservationFormChange = (e) => {
    const { name, value } = e.target;
    setReservationForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitReservation = async (e) => {
    e.preventDefault();
    if (!selectedTime) return;

    try {
      const reservation = await dispatch(
        createReservation({
          ...reservationForm,
          date,
          time: selectedTime,
          party_size: partySize,
        }),
      );
      navigate("/reservations/success", { state: { reservation } });
    } catch {
      // error already surfaced via createError
    }
  };

  return (
    <div className="home-container">
      <h1 className="events-page-title">{t("reservationPageTitle", language)}</h1>

      {/* ===== TABLE RESERVATION ===== */}
      <div className="reservation-card">
        <div className="reservation-step">
          <StepHeader number={1} icon={<FaCalendarAlt className="step-icon" />}>
            {t("dateLabel", language)}
          </StepHeader>
          <input
            type="date"
            className="reservation-input reservation-date-input"
            min={todayStr()}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div className="reservation-step">
          <StepHeader number={2} icon={<FaUsers className="step-icon" />}>
            {t("partySizeLabel", language)}
          </StepHeader>
          <div className="party-size-stepper">
            <button type="button" onClick={() => setPartySize((p) => Math.max(1, p - 1))}>
              –
            </button>
            <span>{partySize}</span>
            <button type="button" onClick={() => setPartySize((p) => Math.min(20, p + 1))}>
              +
            </button>
          </div>
        </div>

        <div className="reservation-step">
          <StepHeader number={3} icon={<FaClock className="step-icon" />}>
            {t("timeLabel", language)}
          </StepHeader>

          {loadingSlots && <div className="reservation-slots-loading skeleton" />}

          {!loadingSlots && slotsError && <Message variant="danger">{slotsError}</Message>}

          {!loadingSlots && !slotsError && slots.length === 0 && (
            <div className="reservation-empty-slots">{t("noSlotsAvailable", language)}</div>
          )}

          {!loadingSlots && slots.length > 0 && (
            <div className="table-chip-row">
              {slots.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  className={`table-chip ${selectedTime === slot ? "active" : ""}`}
                  onClick={() => setSelectedTime(slot)}
                >
                  {slot}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedTime && (
          <form className="reservation-contact-form" onSubmit={submitReservation}>
            <StepHeader number={4} icon={<FaUser className="step-icon" />}>
              {t("yourDetailsLabel", language)}
            </StepHeader>

            {createError && <Message variant="danger">{createError}</Message>}

            <div className="field-row">
              <label className="field-label">{t("guestNameLabel", language)}</label>
              <input
                className="reservation-input"
                name="guest_name"
                value={reservationForm.guest_name}
                onChange={handleReservationFormChange}
                required
              />
            </div>

            <div className="field-row-2">
              <div className="field-row">
                <label className="field-label">{t("guestEmailLabel", language)}</label>
                <input
                  className="reservation-input"
                  type="email"
                  name="guest_email"
                  value={reservationForm.guest_email}
                  onChange={handleReservationFormChange}
                  required
                />
              </div>
              <div className="field-row">
                <label className="field-label">{t("guestPhoneLabel", language)}</label>
                <input
                  className="reservation-input"
                  name="guest_phone"
                  value={reservationForm.guest_phone}
                  onChange={handleReservationFormChange}
                  required
                />
              </div>
            </div>

            <div className="field-row">
              <label className="field-label">{t("guestNoteLabel", language)}</label>
              <textarea
                className="reservation-input"
                name="guest_note"
                rows={2}
                placeholder={t("guestNotePlaceholder", language)}
                value={reservationForm.guest_note}
                onChange={handleReservationFormChange}
              />
            </div>

            <button type="submit" className="reservation-submit-btn" disabled={creatingReservation}>
              {creatingReservation
                ? t("submitting", language)
                : t("confirmReservationButton", language)}
            </button>
          </form>
        )}
      </div>

      {/* ===== UPCOMING EVENTS — always visible below the reservation
          form, not hidden behind a tab ===== */}
      <div className="upcoming-events-section" ref={eventsSectionRef}>
        <h2 className="upcoming-events-title">
          <FaGlassCheers /> {t("upcomingEventsTitle", language)}
        </h2>

        {!selectedSlug && (
          <>
            {loadingEvents && <Loader />}
            {!loadingEvents && eventsError && <Message variant="danger">{eventsError}</Message>}

            {!loadingEvents && !eventsError && events.length === 0 && (
              <EmptyState
                icon={<FaGlassCheers />}
                title={t("emptyEventsTitle", language)}
                message={t("emptyEventsMessage", language)}
              />
            )}

            {!loadingEvents && events.length > 0 && (
              <div className="events-grid">
                {events.map((ev) => (
                  <button
                    key={ev.slug}
                    type="button"
                    className="event-card"
                    onClick={() => openEvent(ev.slug)}
                  >
                    {ev.cover_image && (
                      <div
                        className="event-card-cover"
                        style={{ backgroundImage: `url(${ev.cover_image})` }}
                      />
                    )}

                    <div className="event-card-body">
                      <h3 className="event-card-title">{ev.title}</h3>

                      <div className="event-card-meta">
                        {new Date(ev.date).toLocaleDateString(localeTag, {
                          month: "short",
                          day: "numeric",
                        })}{" "}
                        · {ev.start_time?.slice(0, 5)}
                      </div>

                      <div className="event-card-footer">
                        <span className="event-card-price">
                          {ev.price
                            ? `${ev.price} ${t("eventPriceSuffix", language)}`
                            : t("eventFreeLabel", language)}
                        </span>

                        {ev.is_sold_out ? (
                          <span className="event-card-badge sold-out">
                            {t("eventSoldOut", language)}
                          </span>
                        ) : (
                          <span className="event-card-capacity">
                            <FaUsers />{" "}
                            {t("eventCapacityLabel", language)(ev.booked_count, ev.max_capacity)}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {selectedSlug && (
          <>
            <button type="button" className="event-detail-back" onClick={closeEvent}>
              <FaArrowLeft /> {t("backToEvents", language)}
            </button>

            {loadingEventDetail && <Loader />}

            {!loadingEventDetail && event && (
              <>
                {event.cover_image && (
                  <div
                    className="event-detail-cover"
                    style={{ backgroundImage: `url(${event.cover_image})` }}
                  />
                )}

                <h3 className="event-detail-title">{event.title}</h3>

                <div className="event-detail-meta">
                  {new Date(event.date).toLocaleDateString(localeTag, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}{" "}
                  · {event.start_time?.slice(0, 5)}
                  {event.end_time ? `–${event.end_time.slice(0, 5)}` : ""}
                </div>

                {event.description && (
                  <p className="event-detail-description">{event.description}</p>
                )}

                <div className="event-detail-facts">
                  <span className="event-detail-price">
                    {event.price
                      ? `${event.price} ${t("eventPriceSuffix", language)}`
                      : t("eventFreeLabel", language)}
                  </span>
                  <span className="event-detail-capacity">
                    <FaUsers />{" "}
                    {t("eventCapacityLabel", language)(event.booked_count, event.max_capacity)}
                  </span>
                </div>

                {event.is_sold_out ? (
                  <Message variant="warning">{t("eventSoldOut", language)}</Message>
                ) : (
                  <form className="event-booking-form" onSubmit={submitEventBooking}>
                    {eventReserveError && <Message variant="danger">{eventReserveError}</Message>}

                    <div className="field-row">
                      <label className="field-label">{t("guestNameLabel", language)}</label>
                      <input
                        className="reservation-input"
                        name="guest_name"
                        value={eventForm.guest_name}
                        onChange={handleEventFormChange}
                        required
                      />
                    </div>

                    <div className="field-row-2">
                      <div className="field-row">
                        <label className="field-label">{t("guestEmailLabel", language)}</label>
                        <input
                          className="reservation-input"
                          type="email"
                          name="guest_email"
                          value={eventForm.guest_email}
                          onChange={handleEventFormChange}
                          required
                        />
                      </div>
                      <div className="field-row">
                        <label className="field-label">{t("guestPhoneLabel", language)}</label>
                        <input
                          className="reservation-input"
                          name="guest_phone"
                          value={eventForm.guest_phone}
                          onChange={handleEventFormChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="field-row">
                      <label className="field-label">{t("partySizeLabel", language)}</label>
                      <div className="party-size-stepper">
                        <button
                          type="button"
                          onClick={() =>
                            setEventPartySize((p) => Math.max(event.min_party_size || 1, p - 1))
                          }
                        >
                          –
                        </button>
                        <span>{eventPartySize}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setEventPartySize((p) => Math.min(event.max_party_size || 12, p + 1))
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="field-row">
                      <label className="field-label">{t("guestNoteLabel", language)}</label>
                      <textarea
                        className="reservation-input"
                        name="guest_note"
                        rows={2}
                        placeholder={t("guestNotePlaceholder", language)}
                        value={eventForm.guest_note}
                        onChange={handleEventFormChange}
                      />
                    </div>

                    {event.booking_notes && (
                      <div className="event-booking-notes">{event.booking_notes}</div>
                    )}

                    <button type="submit" className="btn-order" disabled={reservingEvent}>
                      <div className="order-button-text">
                        {reservingEvent
                          ? t("submitting", language)
                          : t("eventBookButton", language)}
                      </div>
                    </button>
                  </form>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ReservationHubScreen;
